// The suite's own right-click menu (web + desktop), mounted once per window. It replaces the browser's / WebView's
// native menu (Shift + right-click still opens the native one, e.g. for „Inspect“). What it offers depends on where the
// click lands (registry.ts): fields and selected text get the text entries (text.ts), everything else what the
// program's providers return, an empty area the program's `fallback`. Look, labels, toast and clipboard come from
// `configureContextMenu` (config.ts). Keys: ↑ ↓ Home End, Enter/Space, → ← for submenus, Esc, first letter.
// Closes on outside click, scroll, resize, window blur, hash change, page unload, Esc.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ctxConfig, ctxToast } from './config';
import { CHEVRON, CtxIconSvg } from './icons';
import { first, groups as joinGroups, isAction, isItem, isSub, onKey, place, placeSub, type CtxAction, type CtxEntry } from './menu';
import { collect, VIEW_EVENT, type CtxEvent, type ViewRequest } from './registry';
import { textEntries, editableOf } from './text';

export { configureContextMenu, DEFAULT_THEME, webClipboard, type CtxConfig, type CtxTheme, type CtxLabels, type CtxClipboard } from './config';
export { registerCtxIcons } from './icons';

interface Props {
  /** Extra entries for selected text outside fields (e.g. „In Dokumenten suchen“). */
  selectionExtra?: (ev: CtxEvent) => CtxEntry[] | null;
  /** Entries for a click where nothing else applies (page-level). Omit for no menu there. */
  fallback?: (ev: CtxEvent) => CtxEntry[] | null;
  /** Gets a function that re-builds the open menu (async providers call it when their data arrived). */
  onRebuild?: (rebuild: () => void) => void;
  /** The big view for `openBigView()` (e.g. RimoPreview). Omitted → no viewer (and no pdf.js in the program's bundle). */
  renderView?: (req: ViewRequest, close: () => void) => ReactNode;
  /** Only the text entries in fields; elsewhere the native menu stays (RimoID's sign-in and password pages). */
  fieldsOnly?: boolean;
}

/** Text the user selected, if the click is on it (right-click elsewhere ignores an old selection). */
function selectionAt(target: Element): string {
  const s = window.getSelection();
  if (!s || s.isCollapsed || !s.rangeCount) return '';
  const t = s.toString();
  try { if (!s.containsNode(target, true)) return ''; } catch { return ''; }
  return t;
}

/** All entries for a right-click (exported for tests). */
export function buildEntries(ev: CtxEvent, extra: Pick<Props, 'selectionExtra' | 'fallback' | 'fieldsOnly'> = {}): CtxEntry[] {
  // Fields: only the text entries (like every native field menu).
  if (editableOf(ev.target)) return joinGroups(textEntries(ev));
  if (extra.fieldsOnly) return [];
  const sel = textEntries(ev);
  const gs = collect(ev);
  const selExtra = ev.selection.trim() ? extra.selectionExtra?.(ev) : null;
  if (!sel && !gs.length && !selExtra) return joinGroups(extra.fallback?.(ev) ?? null);
  return joinGroups(sel, selExtra, ...gs);
}

interface Open { ev: CtxEvent; entries: CtxEntry[] }
/** The open submenu: which row of the main menu, its entries, position and active row. */
interface Sub { index: number; entries: CtxEntry[]; row: { left: number; right: number; top: number }; pos: { left: number; top: number } | null; active: number }

const HOVER_OPEN_MS = 150, HOVER_CLOSE_MS = 250;

export function ContextMenu({ selectionExtra, fallback, onRebuild, renderView, fieldsOnly }: Props) {
  const [open, setOpen] = useState<Open | null>(null);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [sub, setSub] = useState<Sub | null>(null);
  const [focus, setFocus] = useState<'root' | 'sub'>('root');
  const [view, setView] = useState<ViewRequest | null>(null);
  const box = useRef<HTMLDivElement>(null), subBox = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openRef = useRef(open); openRef.current = open;
  const extra = useRef({ selectionExtra, fallback, fieldsOnly }); extra.current = { selectionExtra, fallback, fieldsOnly };

  const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const close = useCallback(() => { clearTimer(); setOpen(null); setPos(null); setActive(-1); setSub(null); setFocus('root'); }, []);
  const run = useCallback((e: CtxAction) => {
    if (e.disabled) return;
    close();
    try {
      const r = e.run();
      if (r && typeof (r as Promise<unknown>).then === 'function') (r as Promise<unknown>).catch((err) => ctxToast(String(err?.message || err), 'warning'));
    } catch (err) { ctxToast(String((err as Error)?.message || err), 'warning'); }
  }, [close]);

  /** Opens the submenu of main-menu row `index` (row element for the position). */
  const openSub = useCallback((index: number, rowEl: Element | null | undefined, focusIt: boolean) => {
    const o = openRef.current; const e = o?.entries[index];
    if (!o || !isSub(e) || e.disabled || !rowEl) return;
    const r = rowEl.getBoundingClientRect();
    setSub((cur) => (cur && cur.index === index ? { ...cur, active: focusIt ? first(e.sub) : cur.active }
      : { index, entries: e.sub, row: { left: r.left, right: r.right, top: r.top }, pos: null, active: focusIt ? first(e.sub) : -1 }));
    if (focusIt) setFocus('sub');
  }, []);
  const rowEl = (i: number) => box.current?.querySelector(`[data-ctx-row="${i}"]`);

  // Re-build the open menu (a provider's data arrived).
  useEffect(() => {
    onRebuild?.(() => {
      const o = openRef.current; if (!o) return;
      setOpen({ ev: o.ev, entries: buildEntries(o.ev, extra.current) });
      setSub(null); setFocus('root');
    });
  }, [onRebuild]);

  useEffect(() => {
    const onCtx = (e: MouseEvent) => {
      if (e.defaultPrevented || e.shiftKey) { if (openRef.current) close(); return; }
      const t = e.target instanceof Element ? e.target : (e.target as Node | null)?.parentElement;
      if (!t) return;
      // Inside our own menu: nothing.
      if (t.closest('[data-ctx-menu]')) { e.preventDefault(); return; }
      // fieldsOnly: outside fields the browser's own menu stays.
      if (extra.current.fieldsOnly && !editableOf(t)) { if (openRef.current) close(); return; }
      e.preventDefault();
      const ev: CtxEvent = { target: t, x: e.clientX, y: e.clientY, selection: selectionAt(t) };
      const entries = buildEntries(ev, extra.current);
      if (!entries.some(isItem)) { close(); return; }
      clearTimer(); setPos(null); setActive(-1); setSub(null); setFocus('root'); setOpen({ ev, entries });
    };
    const onView = (e: Event) => setView((e as CustomEvent<ViewRequest>).detail);
    document.addEventListener('contextmenu', onCtx);
    window.addEventListener(VIEW_EVENT, onView);
    return () => { document.removeEventListener('contextmenu', onCtx); window.removeEventListener(VIEW_EVENT, onView); };
  }, [close]);

  // While open: keys, outside clicks, scroll, resize, blur.
  useEffect(() => {
    if (!open) return;
    const inMenu = (n: EventTarget | null) => n instanceof Node && (!!box.current?.contains(n) || !!subBox.current?.contains(n));
    const onDown = (e: MouseEvent) => { if (!inMenu(e.target)) close(); };
    const onScroll = (e: Event) => { if (!inMenu(e.target)) close(); };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) { close(); return; } // ⌘C etc. keep working on the field
      const inSub = focus === 'sub' && !!sub;
      const entries = inSub ? sub!.entries : open.entries, cur = inSub ? sub!.active : active;
      const r = onKey(entries, cur, e.key);
      if (r.kind === 'none') { if (e.key.length === 1) { e.preventDefault(); e.stopImmediatePropagation(); } return; }
      e.preventDefault(); e.stopImmediatePropagation();
      if (r.kind === 'move') { if (inSub) setSub({ ...sub!, active: r.to }); else { setActive(r.to); setSub(null); } }
      else if (r.kind === 'open') { if (!inSub) openSub(r.index, rowEl(r.index), true); }
      else if (r.kind === 'close') {
        // In a submenu ← / Esc go back to the parent row; Tab closes everything.
        if (inSub && e.key !== 'Tab') { setSub(null); setFocus('root'); } else close();
      } else { const it = entries[r.index]; if (isAction(it)) run(it); }
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('wheel', onScroll as EventListener, { capture: true, passive: true });
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    window.addEventListener('hashchange', close);
    window.addEventListener('beforeunload', close);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('wheel', onScroll as EventListener, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
      window.removeEventListener('hashchange', close);
      window.removeEventListener('beforeunload', close);
    };
  }, [open, active, sub, focus, close, run, openSub]);

  // Measure, then place inside the viewport.
  useLayoutEffect(() => {
    if (!open || !box.current) return;
    // offsetWidth/Height: the opening animation scales the box, getBoundingClientRect would be a few px short.
    const el = box.current;
    setPos(place(open.ev.x, open.ev.y, el.offsetWidth, el.offsetHeight, window.innerWidth, window.innerHeight));
  }, [open]);
  useLayoutEffect(() => {
    if (!sub || sub.pos || !subBox.current) return;
    const el = subBox.current;
    setSub({ ...sub, pos: placeSub(sub.row, el.offsetWidth, el.offsetHeight, window.innerWidth, window.innerHeight) });
  }, [sub]);
  useEffect(() => () => clearTimer(), []);

  // Pointer: a submenu row opens its submenu after a short pause; another row closes it after a slightly longer one,
  // so moving diagonally into the submenu doesn't close it.
  const enterRoot = (i: number, e: CtxEntry, el: Element) => {
    setActive(isItem(e) && !e.disabled ? i : -1); setFocus('root'); clearTimer();
    if (isSub(e) && !e.disabled) { if (sub?.index !== i) timer.current = setTimeout(() => openSub(i, el, false), HOVER_OPEN_MS); }
    else if (sub) timer.current = setTimeout(() => setSub(null), HOVER_CLOSE_MS);
  };

  return (
    <>
      {open && createPortal(
        <>
          <Panel refEl={box} entries={open.entries} active={active} pos={pos} openSubIndex={sub?.index ?? -1}
            onEnter={enterRoot} onLeave={() => { if (!sub) setActive(-1); }}
            onClick={(i, e, el) => { if (isSub(e)) { clearTimer(); openSub(i, el, true); } else if (isAction(e)) run(e); }} />
          {sub && (
            <Panel refEl={subBox} entries={sub.entries} active={sub.active} pos={sub.pos} openSubIndex={-1}
              onEnter={(i, e) => { clearTimer(); setFocus('sub'); setActive(sub.index); setSub({ ...sub, active: isAction(e) && !e.disabled ? i : -1 }); }}
              onLeave={() => setSub({ ...sub, active: -1 })}
              onClick={(_i, e) => { if (isAction(e)) run(e); }} />
          )}
        </>,
        document.body,
      )}
      {view && renderView?.(view, () => setView(null))}
    </>
  );
}

/** One menu surface (main menu or submenu). */
function Panel({ refEl, entries, active, pos, openSubIndex, onEnter, onLeave, onClick }: {
  refEl: React.RefObject<HTMLDivElement | null>; entries: CtxEntry[]; active: number; pos: { left: number; top: number } | null; openSubIndex: number;
  onEnter(i: number, e: CtxEntry, el: Element): void; onLeave(): void; onClick(i: number, e: CtxEntry, el: Element): void;
}) {
  const { theme: T, noTranslate, zIndex } = ctxConfig();
  // No icon column when nothing in this panel has an icon (rows line up either way).
  const icons = T.icons && entries.some((e) => isItem(e) && !!e.icon), H = T.header;
  return (
    <div ref={refEl} data-ctx-menu="1" data-no-tr={noTranslate ? '1' : undefined} translate={noTranslate ? 'no' : undefined} role="menu" aria-orientation="vertical"
      onMouseDown={(e) => e.preventDefault() /* keep focus + selection in the field */}
      onMouseLeave={onLeave}
      style={{ position: 'fixed', left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden', zIndex,
        minWidth: T.minWidth, maxWidth: 340, maxHeight: 'calc(100vh - 12px)', overflowY: 'auto', boxSizing: 'border-box',
        borderRadius: T.radius, background: T.bg, border: '1px solid ' + T.line, boxShadow: T.shadow,
        padding: T.padding, display: 'flex', flexDirection: 'column', gap: T.gap, font: T.font, color: T.fg,
        userSelect: 'none', WebkitUserSelect: 'none', animation: T.animate ? 'rc-ctx-in .09s ease-out' : undefined }}>
      {T.animate && <style>{'@keyframes rc-ctx-in{from{opacity:0;transform:scale(.98)}to{opacity:1;transform:none}}'}</style>}
      {entries.map((e, i) => {
        if (e === 'sep') return <div key={i} role="separator" style={{ height: 1, background: T.sep, margin: T.sepInset, flex: 'none' }} />;
        if (!isItem(e)) return <div key={i} title={e.header} style={{ padding: H.padding, marginBottom: H.marginBottom, borderBottom: H.rule ? '1px solid ' + T.sep : undefined, font: T.headerFont, ...(T.headerUpper ? { letterSpacing: '.14em', textTransform: 'uppercase' } : {}), color: H.color ?? T.mute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 'none' }}>{e.header}</div>;
        const on = (i === active || i === openSubIndex) && !e.disabled, danger = isAction(e) && e.danger;
        return (
          <div key={i} data-ctx-row={i} role="menuitem" aria-disabled={e.disabled || undefined} aria-haspopup={isSub(e) ? 'menu' : undefined} aria-expanded={isSub(e) ? i === openSubIndex : undefined}
            onMouseEnter={(ev) => onEnter(i, e, ev.currentTarget)} onClick={(ev) => onClick(i, e, ev.currentTarget)}
            style={{ height: T.rowHeight, flex: 'none', padding: icons ? '0 10px 0 8px' : '0 10px', borderRadius: T.rowRadius, display: 'flex', alignItems: 'center', gap: 10,
              background: on ? (danger ? T.dangerHover ?? T.hover : T.hover) : 'transparent', color: danger ? T.danger : on ? T.fg : T.rowFg ?? T.fg, opacity: e.disabled ? 0.38 : 1, cursor: e.disabled ? 'default' : 'pointer' }}>
            {icons && <span style={{ width: Math.max(16, T.iconSize), display: 'flex', justifyContent: 'center', color: on ? T.fg : T.dim, flex: 'none' }}>{e.icon ? <CtxIconSvg name={e.icon} size={T.iconSize} stroke={T.iconStroke} /> : null}</span>}
            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.label}</span>
            {e.hint && <span style={{ font: T.hintFont, color: T.mute, maxWidth: 130, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 'none' }}>{e.hint}</span>}
            {isSub(e) && <span style={{ display: 'flex', color: on ? T.fg : T.dim, flex: 'none', marginRight: -4 }}>{CHEVRON}</span>}
          </div>
        );
      })}
    </div>
  );
}
